<template>
    <div>
        <!-- Loader Component -->
        <Loader :visible="isLoading" :text="loadingText" />

        <!-- Confirmation Modal for Invoiced -->
        <ConfirmationModal :show="showInvoicedConfirmation" @close="showInvoicedConfirmation = false">
            <template #title>Conferma Fatturazione</template>

            <template #content>
                Sei sicuro di voler contrassegnare i contratti selezionati come fatturati?
                <br><br>
                <strong>{{ selectedContracts.length }} contratto/i</strong> verranno contrassegnati come fatturati.
            </template>

            <template #footer>
                <button 
                    @click="showInvoicedConfirmation = false" 
                    class="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 mr-2"
                >
                    Annulla
                </button>
                <button 
                    @click="confirmMarkAsInvoiced" 
                    class="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
                >
                    Conferma
                </button>
            </template>
        </ConfirmationModal>

        <!-- Confirmation Modal for Issue Orders -->
        <ConfirmationModal :show="showIssueConfirmation" @close="showIssueConfirmation = false">
            <template #title>Conferma Emissione Ordine</template>

            <template #content>
                Sei sicuro di voler procedere con l'emissione degli ordini dei contratti selezionati?
                <br><br>
                <strong>{{ selectedContracts.length }} contratto/i</strong> verranno processati per l'emissione.
            </template>

            <template #footer>
                <button 
                    @click="showIssueConfirmation = false" 
                    class="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 mr-2"
                >
                    Annulla
                </button>
                <button 
                    @click="confirmIssueSelectedContracts" 
                    class="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                >
                    Conferma
                </button>
            </template>
        </ConfirmationModal>

        <!-- Message Modal -->
        <!--<ModalMessage 
            v-if="showMessageModal" 
            :message="messageContent" 
            :type="messageType" 
            @close="showMessageModal = false" 
        />-->

        <!-- Success Message Modal -->
        <ModalMessage
            :visible="showSuccessMessage"
            :title="successTitle"
            :messages="successMessages"
            @close="showSuccessMessage = false"
        />

        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>
        <div class="p-6">
            <!-- Filtri di ricerca -->
            <div class="mb-4 bg-white rounded-md shadow-lg">
                <div @click="toggleSection('searchFilters')"
                    class="flex justify-between items-center px-6 py-3 cursor-pointer bg-gray-100 rounded-t-md">
                    <h3 class="font-semibold">Filtri di Ricerca</h3>
                    <span class="transform transition-transform" :class="{ 'rotate-180': !sections.searchFilters }">
                        &#9660;<!-- Black down arrow Unicode character -->
                    </span>
                </div>

                <div v-show="sections.searchFilters" class="p-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                        <!-- Contratto -->
                        <div class="mb-4">
                            <label class="block text-gray-700 mb-2">Codice Contratto</label>
                            <input v-model="filters.DocNo" type="text" class="p93__input w-full" placeholder="Codice Contratto" @keyup.enter="searchContracts"/>
                        </div>

                        <!-- Modello contratto -->
                        <div class="mb-4">
                            <label class="block text-gray-700 mb-2">Modello</label>
                            <v-select 
                                :options="contractModels"
                                v-model="filters.model"
                                label="name"
                                placeholder="Seleziona modello contratto..." 
                                class="w-full"
                            >
                            </v-select>
                        </div>

                        <!-- Tipo contratto -->
                        <div class="mb-4">
                            <label class="block text-gray-700 mb-2">Tipo contratto</label>
                            <v-select 
                                :options="contractTypes"
                                v-model="filters.contractType"
                                label="name"
                                placeholder="Seleziona tipo contratto..." 
                                class="w-full"
                            >
                            </v-select>
                        </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <!-- Cliente -->
                        <div class="mb-4">
                            <label class="block text-gray-700 mb-2">Cliente</label>
                            <v-select :options="foundClients" :filterable="false"
                                :modelValue="filters.customer"
                                @update:modelValue="onCustomerChange"
                                label="name" @search="searchClients" placeholder="Cerca cliente..." class="w-full">
                                <template #no-options="{ search, searching }">
                                    <div v-if="searching">🔍 Ricerca in corso...</div>
                                    <div v-else-if="!search">Inizia a digitare per cercare un cliente...</div>
                                </template>
                            </v-select>
                        </div>

                        <template v-if="!filters.customer">
                            <!-- Da cliente -->
                            <div class="mb-4">
                                <label class="block text-gray-700 mb-2">Da cliente</label>
                                <input v-model="filters.fromClient" type="text" class="p93__input w-full"
                                    placeholder="Da cliente" @keyup.enter="searchContracts"/>
                                <div v-if="searchForm.errors.fromClient"
                                    class="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                                    {{ searchForm.errors.fromClient }}
                                </div>
                            </div>

                            <!-- A cliente -->
                            <div class="mb-4">
                                <label class="block text-gray-700 mb-2">A cliente</label>
                                <input v-model="filters.toClient" type="text" class="p93__input w-full"
                                    placeholder="A Cliente" @keyup.enter="searchContracts"/>
                                <div v-if="searchForm.errors.toClient"
                                    class="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                                    {{ searchForm.errors.toClient }}
                                </div>
                            </div>
                        </template>

                        <template v-else>
                            <!-- Sede contratto -->
                            <div class="mb-4 lg:col-span-2">
                                <label class="block text-gray-700 mb-2">Sede contratto</label>
                                <v-select
                                    :options="sitesOptions"
                                    v-model="filters.branch"
                                    label="name"
                                    placeholder="Tutte le sedi"
                                    class="w-full"
                                />
                            </div>
                        </template>

                        <!-- Range di date -->
                        <div class="mb-4">
                            <label class="block text-gray-700 mb-2">Periodo</label>
                            <VueDatePicker v-model="filters.dateRange" range locale="it" format="dd/MM/yyyy" auto-apply
                                placeholder="Seleziona intervallo date" :enable-time-picker="false" text-input
                                class="w-full border border-neutral-300 rounded-md shadow-md" @input="pageDateInputRange"/>
                        </div>
                    </div>
                    <!-- Bottoni cerca e reset -->
                    <div class="flex justify-end mt-4 space-x-2">
                        <button @click="resetFilters"
                            class="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600"
                            :disabled="searchForm.processing">
                            <i class="fa fa-undo mr-2"></i>
                            Reset
                        </button>
                        <button @click="searchContracts"
                            class="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600"
                            :disabled="searchForm.processing">
                            <i class="fa fa-search mr-2"></i>
                            {{ searchForm.processing ? 'Ricerca in corso...' : 'Cerca' }}
                        </button>
                    </div>

                    <!-- Messaggio di errore per filtri -->
                    <div v-if="searchForm.errors.filters"
                        class="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                        <i class="fa fa-exclamation-triangle mr-2"></i>
                        {{ searchForm.errors.filters }}
                    </div>
                </div>
            </div>

            <!-- Istruzioni per l'utente (mostra solo se non è stata fatta ricerca) -->
            <div v-if="!hasSearched" class="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div class="flex items-start">
                    <i class="fa fa-info-circle text-blue-500 mt-1 mr-3"></i>
                    <div>
                        <h4 class="text-blue-800 font-medium mb-1">Come utilizzare la ricerca</h4>
                        <ul class="text-blue-700 text-sm space-y-1">
                            <li>• Inserisci il <strong>codice contratto</strong> per cercare un contratto specifico</li>
                            <li>• Seleziona un singolo <strong>cliente</strong> OPPURE specifica una combinazione "da"/"a" che racchiuda i clienti voluti (es. per ricercare tutti quelli che iniziano con la lettera a, usare 'a' e 'aÿ', così da considerare anche caratteri speciali)</li>
                            <li>• Oppure <strong>modello</strong> o <strong>tipo</strong> contratto, o un <strong>range</strong> di date.</li>
                            <li>• È necessario compilare almeno uno dei campi presenti</li>
                            <li>• Quando selezioni le righe, usa il pulsante shift/maiusc (<i class="fa fa-arrow-up"></i>) mentre clicchi su una box per selezionare righe multiple</li>
                            <li>• Le righe verranno processate nell'ordine mostrato a schermo</li>
                        </ul>
                    </div>
                </div>
            </div>

            <!-- Risultati -->
            <div v-if="contracts.length > 0 || hasSearched" class="bg-white rounded-md shadow-lg">
                <div class="flex justify-between items-center p-4 border-b">
                    <div>
                        <h3 class="text-lg font-semibold">Risultati Ricerca</h3>
                        <p class="text-gray-600">{{ contracts.length }} contratto/i trovato/i</p>
                    </div>
                    <div v-if="hasSelectedContracts">
                        <div class="flex items-center space-x-2">
                            <div class="flex items-center space-x-2">
                                <label class="text-sm font-medium text-gray-700">Data emissione:</label>
                                <VueDatePicker 
                                    v-model="issueDate" 
                                    locale="it" 
                                    format="dd/MM/yyyy" 
                                    auto-apply
                                    :enable-time-picker="false"
                                    class="w-40"
                                    placeholder="Seleziona data"
                                    text-input
                                    @input="pageDateInput"
                                />
                            </div>
                            <button @click="issueSelectedContracts" class="p93__btn_primary">
                                <i class="fa fa-file-export"></i>&nbsp;
                                <span>Emetti ordine ({{ selectedContracts.length }})</span>
                            </button>
                            <button @click="markAsInvoiced" class="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600">
                                <i class="fa fa-check"></i>&nbsp;
                                <span>Fatturati ({{ selectedContracts.length }})</span>
                            </button>
                        </div>
                    </div>
                </div>
                <div v-if="contracts.length === 0 && hasSearched" class="p-8 text-center text-gray-500">
                    Nessun contratto trovato con i filtri specificati.
                </div>
                <div v-else>
                    <!-- Container che occupa tutto lo spazio disponibile -->
                    <div class="overflow-y-auto" style="height: calc(90vh - 290px); min-height: 300px;">
                        <table class="min-w-full text-sm">
                            <!-- Header fisso -->
                            <thead class="bg-gray-100 sticky top-0 z-10 text-sm">
                                <tr>
                                    <th class="py-3 px-4 border-b text-center bg-gray-100 w-12">
                                        <input type="checkbox" v-model="selectAll" @change="toggleSelectAll"
                                            class="rounded border-gray-300 text-blue-600 focus:ring-blue-500">
                                    </th>
                                    <th class="py-3 px-4 border-b text-left bg-gray-100">Nr Contratto</th>
                                    <th class="py-3 px-4 border-b text-left bg-gray-100">Tipo</th>
                                    <th class="py-3 px-4 border-b text-left bg-gray-100">Modello</th>
                                    <th class="py-3 px-4 border-b text-left bg-gray-100">Cliente</th>
                                    <th class="py-3 px-4 border-b text-left bg-gray-100">Articolo</th>
                                    <th class="py-3 px-4 border-b text-left bg-gray-100">Rata nr.</th>
                                    <th class="py-3 px-4 border-b text-left bg-gray-100">Validità rata</th>
                                    <th class="py-3 px-4 border-b text-right bg-gray-100">Totale</th>
                                </tr>
                            </thead>
                            <tbody class="bg-white">
                                <tr v-for="contract in contracts" :key="contract.BillingId" class="hover:bg-gray-50">
                                    <td class="py-3 px-4 border-b text-center">
                                        <input type="checkbox" :checked="selectedContracts.includes(contract.BillingId)"
                                            @click="toggleContractSelection(contract.BillingId, $event)"
                                            class="rounded border-gray-300 text-blue-600 focus:ring-blue-500">
                                        <!-- Hidden fields for selected contracts -->
                                        <template v-if="selectedContracts.includes(contract.BillingId)">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][Customer]`" :value="contract.Customer">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][PaymentCondition]`" :value="contract.PaymentCondition">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][SalePerson]`" :value="contract.SalePerson">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][AreaManager]`" :value="contract.AreaManager">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][ContractPlace]`" :value="contract.ContractPlace">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][DocNo]`" :value="contract.DocNo">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][StartDate]`" :value="contract.StartDate">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][EndDate]`" :value="contract.EndDate">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][InstallmentNo]`" :value="contract.InstallmentNo">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][DueDate]`" :value="contract.DueDate">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][IstatMarkup]`" :value="contract.IstatMarkup">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][ContractType]`" :value="contract.ContractType">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][StipulationDate]`" :value="contract.StipulationDate">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][BillingPeriod]`" :value="contract.BillingPeriod">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][DiscountFormula]`" :value="contract.DiscountFormula">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][IsGood]`" :value="contract.IsGood">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][Job]`" :value="contract.Job">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][BillingId]`" :value="contract.BillingId">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][Amount]`" :value="contract.Amount">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][Item]`" :value="contract.Item">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][Description]`" :value="contract.Description">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][Qty]`" :value="contract.Qty">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][UoM]`" :value="contract.UoM">
                                            <input type="hidden" :name="`selected_contracts[${contract.BillingId}][UnitValue]`" :value="contract.UnitValue">
                                        </template>
                                    </td>
                                    <td class="py-3 px-4 border-b">{{ contract.DocNo }}</td>
                                    <td class="py-3 px-4 border-b">
                                        <span class="px-2 py-1 text-xs rounded-full"
                                            :class="getContractTypeClass(contract.ContractType)">
                                            {{ getContractTypeLabel(contract.ContractType) }}
                                        </span>
                                    </td>
                                    <td class="py-3 px-4 border-b">{{ contract.Model || '-' }}</td>
                                    <td class="py-3 px-4 border-b">{{ contract.CompanyName }}</td>
                                    <td class="py-3 px-4 border-b">{{ contract.Item }}</td>
                                    <td class="py-3 px-4 border-b">{{ contract.InstallmentNo }}</td>
                                    <td class="py-3 px-4 border-b">{{ formatDate(contract.DueDate) }}</td>
                                    <td class="py-3 px-4 border-b text-right">
                                        <span>
                                        {{ formatCurrency(parseFloat(contract.Amount)) }}
                                        </span>
                                        <br v-if="contract.DiscountFormula && contract.DiscountFormula !== '0'">
                                        <span v-if="contract.DiscountFormula && contract.DiscountFormula !== '0'">
                                        Sc: {{ contract.DiscountFormula }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Link, router, useForm } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Loader from '@/Components/Loader.vue';
import ConfirmationModal from '@/Components/ConfirmationModal.vue';
import ModalMessage from '@/Components/ModalMessage.vue';
import { useLoader } from '@/Composables/useLoader.js';
import axios from 'axios';
import { format, parseISO } from 'date-fns';

export default {
    props: ['contractModels', 'initialFilters', 'orders'],
    components: {
        Link,
        BreadCrumbs,
        Loader,
        ConfirmationModal,
        ModalMessage
    },
    setup() {
        const { isLoading, loadingText, showLoader, hideLoader, withLoader, withLoaderSafe, withInertiaLoader } = useLoader();

        return {
            isLoading,
            loadingText,
            showLoader,
            hideLoader, withLoader,
            withLoaderSafe,
            withInertiaLoader
        };
    },
    data() {
        return {
            searchForm: useForm({
                dateRange: null,
                contractType: null,
                model: null,
                customer: null,
                fromClient: '',
                toClient: '',
                DocNo: '',
                branch: null,
            }),
            foundClients: [],
            sites: [],
            contracts: [],
            selectedContracts: [],
            selectAll: false,
            hasSearched: false,
            issueDate: new Date(), // Add issueDate with default value
            sections: {
                searchFilters: true
            },
            filters: {
                dateRange: this.initialFilters?.dateRange ? [
                    new Date(this.initialFilters.dateRange.start),
                    new Date(this.initialFilters.dateRange.end)
                ] : null,
                contractType: this.initialFilters?.contractType || null,
                model: this.initialFilters?.model || null,
                customer: this.initialFilters?.customer || null,
                fromClient: this.initialFilters?.fromClient || '',
                toClient: this.initialFilters?.toClient || '',
                DocNo: this.initialFilters?.DocNo || '',
                branch: null,
            }, navigation: [
                { name: 'Dashboard', href: route('dashboard') },
                { name: 'Contratti', href: route('modules.home', 'contratti') },
                { name: 'Emissione', href: route('modules.contratti.emissione') }
            ],
            contractTypes: [
                { value: 'SER', name: 'Servizi' },
                { value: 'NOL', name: 'Noleggio' },
                { value: 'PCO', name: 'Pacchetto Ore' }
            ],
            showInvoicedConfirmation: false,
            showIssueConfirmation: false,
            showSuccessMessage: false,
            successTitle: '',
            successMessages: [],
            lastindexSelected: -1,
        };
    },
    computed: {
        hasSelectedContracts() {
            return this.selectedContracts.length > 0;
        },
        selectableContracts() {
            return this.contracts;
        },
        sitesOptions() {
            return [{ id: null, name: 'Tutte le sedi' }, ...(this.sites || [])];
        }
    },

    methods: {
        toggleSection(section) {
            this.sections[section] = !this.sections[section];
        },
        onCustomerChange(val) {
            this.filters.customer = val;
            if (val) {
                this.fetchSites(val);
            } else {
                this.sites = [];
                this.filters.branch = null;
            }
        },
        fetchSites(customer) {
            axios.get(route('modules.contratti.sedi', customer.id))
                .then((response) => {
                    this.sites = response.data.map(item => {
                        const company = item.CompanyName ? item.CompanyName + " - " : "";
                        const name = `${company}${item.Address} ${item.ZIPCode} ${item.City} ${item.County}`;
                        return { id: item.id, name };
                    });
                });
        },
        searchClients(search, loading) {
            if (search.length >= 2) {
                loading(true); clearTimeout(this.searchContactsTimeout);
                this.searchContactsTimeout = setTimeout(() => {
                    axios
                        .get(route('modules.contratti.ricerca-clienti', search))
                        .then((response) => {
                            this.foundClients = response.data;
                            loading(false);
                        });
                }, 300);
            }
        },
        async searchContracts() {
            this.hasSearched = true;

            // Reset checkbox selections on new search
            this.selectedContracts = [];
            this.selectAll = false;

            // Aggiorna i dati del form con i filtri correnti
            this.searchForm.dateRange = this.filters.dateRange ? {
                start: format(this.filters.dateRange[0], 'yyyy-MM-dd'),
                end: format(this.filters.dateRange[1], 'yyyy-MM-dd')
            } : null;
            this.searchForm.contractType = this.filters.contractType?.value || null;
            this.searchForm.model = this.filters.model?.id || null;
            this.searchForm.customer = this.filters.customer?.id || null;
            this.searchForm.fromClient = this.filters.fromClient;
            this.searchForm.toClient = this.filters.toClient;
            this.searchForm.DocNo = this.filters.DocNo;
            this.searchForm.branch = this.filters.branch?.id || null;
            const result = await this.withInertiaLoader(
                this.searchForm,
                route('modules.contratti.cerca-contratti-per-emissione'),
                {
                    preserveUrl: true,
                    onSuccess: (response) => {
                        this.contracts = response.props.contracts || [];
                        this.sections.searchFilters = false;
                    }
                },
                'Ricerca contratti in corso...'
            );

            // Opzionale: gestisci il risultato se necessario
            if (!result.success) {
                // Gli errori sono già gestiti da Inertia, non serve fare nulla
            }
        },
        resetFilters() {
            // Reset all filters to their initial state
            this.filters = {
                dateRange: null,
                contractType: null,
                model: null,
                customer: null,
                fromClient: '',
                toClient: '',
                DocNo: '',
                branch: null,
            };
            this.sites = [];

            // Reset del form Inertia
            this.searchForm.reset();
            this.searchForm.clearErrors();

            // Clear search results
            this.contracts = [];
            this.selectedContracts = [];
            this.selectAll = false;
            this.hasSearched = false;
            this.foundClients = [];
        },
        toggleSelectAll() {
            if (this.selectAll) {
                this.selectedContracts = this.selectableContracts.map(contract => contract.BillingId);
            } else {
                this.selectedContracts = [];
            }
        },
        updateSelectAll() {
            this.selectAll = this.selectedContracts.length === this.selectableContracts.length && this.selectableContracts.length > 0;
        },
        toggleContractSelection(contractId, event) {
            const index = this.selectedContracts.indexOf(contractId);
            if (index > -1) {
                this.selectedContracts.splice(index, 1);
                this.lastindexSelected = -1;
            } else {
                const currentIndex = this.contracts.indexOf(this.contracts.find(c => c.BillingId === contractId));
                if (event.shiftKey && this.lastindexSelected !== -1) {
                    // Se Shift è premuto, seleziona l'intervallo
                    for (let i = Math.min(this.lastindexSelected, currentIndex); i <= Math.max(this.lastindexSelected, currentIndex); i++) {
                        const id = this.contracts[i].BillingId;
                        if (!this.selectedContracts.includes(id)) {
                            this.selectedContracts.push(id);
                        }
                    }
                }
                else {
                    this.selectedContracts.push(contractId);
                }
                this.lastindexSelected = currentIndex;
                
            }
            this.updateSelectAll();
        },
        async issueSelectedContracts() {
            if (this.selectedContracts.length === 0) {
                alert('Seleziona almeno un contratto per l\'emissione');
                return;
            }

            this.showIssueConfirmation = true;
        },
        async confirmIssueSelectedContracts() {
            this.showIssueConfirmation = false;

            // Prepare data for API call
            const contractsData = [];
             this.selectedContracts.sort((a,b) => this.contracts.indexOf(this.contracts.find(x => x.BillingId == a)) - this.contracts.indexOf(this.contracts.find(x => x.BillingId == b))).forEach((contractId) => {
                const contract = this.contracts.find(c => c.BillingId === contractId);
                if (contract) {
                    contractsData.push({
                        Customer: contract.Customer,
                        PaymentCondition: contract.PaymentCondition,
                        SalePerson: contract.SalePerson,
                        AreaManager: contract.AreaManager,
                        ContractPlace: contract.ContractPlace,
                        DocNo: contract.DocNo,
                        StartDate: contract.StartDate,
                        EndDate: contract.EndDate,
                        InstallmentNo: contract.InstallmentNo,
                        DueDate: contract.DueDate,
                        IstatMarkup: contract.IstatMarkup,
                        ContractType: contract.ContractType,
                        Model: contract.Model,
                        StipulationDate: contract.StipulationDate,
                        BillingPeriod: contract.BillingPeriod,
                        DiscountFormula: contract.DiscountFormula,
                        IsGood: contract.IsGood,
                        Job: contract.Job,
                        BillingId: contract.BillingId,
                        RowId: contract.RowId,
                        Amount: contract.Amount,
                        Item: contract.Item,
                        Description: contract.Description,
                        Qty: contract.Qty,
                        UoM: contract.UoM,
                        UnitValue: contract.UnitValue,
                        CopyCost: contract.CopyCost,
                        CopyBalance: contract.CopyBalance,
                        InvoicedAmount: contract.InvoicedAmount,
                        HourPackageId: contract.HourPackageId,
                    });
                }
            });

            let customers = [...new Set(contractsData.map(x => x.Customer))];

            const result = await this.withLoaderSafe(
                async () => {
                    const response = await axios.post(route('modules.contratti.emetti-ordini'), {
                        issueDate: format(this.issueDate, 'yyyy-MM-dd'),
                        contracts: contractsData
                    });
                    return response;
                },
                `Emissione in corso di ${this.selectedContracts.length} righe contratti di ${customers.length} clienti diversi...`,
                (error) => {
                    console.error('Errore durante l\'emissione:', error);
                    this.successTitle = 'Errore';
                    this.successMessages = ['Si è verificato un errore durante l\'emissione degli ordini'];
                    this.showSuccessMessage = true;
                }
            );

            if (result) {
                // Reset selection after successful emission
                this.selectedContracts = [];
                this.selectAll = false;

                if (result.data.error == undefined)
                {
                    // Show success message
                    this.successTitle = 'Emissione Completata';
                    this.successMessages = [
                        `${result.data.count || 0} ordini sono stati emessi con successo.`,
                    ];
                }             
                else {
                    this.successTitle = 'Emissione Completata con errori';
                    this.successMessages = [
                        `${result.data.count || 0} ordini sono stati emessi con successo. Errore durante l'emissione del contratto di ${result.data.error.customer.MA_CustSupp.CustSupp}`
                    ];

                    console.log('Dettagli errore:', result.data.error);
                }

                this.showSuccessMessage = true;

                // Refresh the contracts list to remove emitted contracts
                await this.searchContracts();
            }
        },
        markAsInvoiced() {
            if (this.selectedContracts.length === 0) {
                alert('Seleziona almeno un contratto da segnare come fatturato');
                return;
            }

            this.showInvoicedConfirmation = true;
        },
        async confirmMarkAsInvoiced() {
            this.showInvoicedConfirmation = false;

            // Prepare data for API call
            const contractsData = [];
            this.selectedContracts.sort((a,b) => this.contracts.indexOf(this.contracts.find(x => x.BillingId == a)) - this.contracts.indexOf(this.contracts.find(x => x.BillingId == b))).forEach((contractId) => {
                const contract = this.contracts.find(c => c.BillingId === contractId);
                if (contract) {
                    contractsData.push({
                        BillingId: contract.BillingId,
                        Item: contract.Item,
                        CopyCost: contract.CopyCost,
                        CopyBalance: contract.CopyBalance,
                        InvoicedAmount: contract.InvoicedAmount,
                        RowId: contract.RowId,
                    });
                }     
            });

            const result = await this.withLoaderSafe(
                async () => {
                    const response = await axios.post(route('modules.contratti.segna-come-fatturati'), {
                        contracts: contractsData
                    });
                    return response;
                },
                `Modifica in corso per ${this.selectedContracts.length} contratto/i...`,
                (error) => {
                    console.error('Errore durante la modifica:', error);
                    this.successTitle = 'Errore';
                    this.successMessages = ['Si è verificato un errore durante la modifica dei contratti'];
                    this.showSuccessMessage = true;
                }
            );

            if (result) {
                // Reset selection after successful operation
                this.selectedContracts = [];
                this.selectAll = false;

                // Show success message
                this.successTitle = 'Modifica Completata';
                this.successMessages = [
                    `${result.data.count || 0} contratti sono stati contrassegnati come fatturati.`,
                ];
                this.showSuccessMessage = true;

                // Refresh the contracts list
                await this.searchContracts();
            }
        },
        getContractTypeLabel(type) {
            const types = {
                'SER': 'Servizi',
                'NOL': 'Noleggio'
            };
            return types[type] || type;
        }, getContractTypeClass(type) {
            const classes = {
                'SER': 'bg-blue-100 text-blue-800',
                'NOL': 'bg-green-100 text-green-800'
            };
            return classes[type] || 'bg-gray-100 text-gray-800';
        },
    }
};
</script>